// interfaces.ts
interface FileItem {
  name: string;
  downloadUrl?: string;
  fileUrl?: string;
  serverRelativeUrl: string;
  taskFile: boolean;
  propertyFile: boolean;
  commentFile: boolean;
  componentFile: boolean;
  fileId?: number;
  commentId?: number;
  siteId?: number;
  componentId?: number;
}

interface FileResultItem {
  fileBytes: string;
  fileName: string;
  fileType: string;
}

interface FileResponse {
  hasError: boolean;
  feedback: string;
  fileResultItem: FileResultItem;
}

// file-viewer.component.ts
import {
  Component,
  ViewChild,
  Output,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { DxButtonModule, DxPopupModule } from "devextreme-angular";
import { DocumentsService } from "src/app/services/documents.service";
import { AppGlobals } from "../../app.globals";
import { SharedPipesModule } from "src/app/pipes/shared.pipes.module";
import { AppConfig } from "src/app/app.config";
import dxPopup from "devextreme/ui/popup";
import { ApiBaseService } from "src/app/services/api-base.service";

@Component({
    selector: "app-file-viewer",
    imports: [CommonModule, SharedPipesModule, DxButtonModule, DxPopupModule],
    templateUrl: "./file-viewer.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./file-viewer.component.css"]
})
export class FileViewerComponent implements OnInit, OnDestroy {
  readonly FILE_TYPES = {
    GENERAL: "general",
    IMAGE: "image",
    DOCUMENT: "document",
    PDF: "pdf",
  } as const;

  readonly MIME_TYPES = {
    png: "image/png",
    jpeg: "image/jpeg",
    csv: "text/csv",
    txt: "text/plain",
    pdf: "application/pdf",
    json: "application/json",
    zip: "application/zip",
    xls: "application/vnd.ms-excel",
    ppt: "application/vnd.ms-powerpoint",
    odt: "application/vnd.oasis.opendocument.text",
    pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  } as const;

  fileURL: string = "";
  popupVisible = false;
  documentVisible = false;
  imageVisible = false;
  pdfVisible = false;
  generalVisible = false;

  fileData: { fileType: string; fileUrl: string } = {
    fileType: "",
    fileUrl: "",
  };
  currentFileItem: FileItem | null = null;
  currentAccessToken = "";

  fileDisplay: string;

  popupSizeWidth = 800;
  popupSizeHeight = 600;
  readonly popupSizeWidthLarge = 800;
  readonly popupSizeHeightLarge = 600;
  readonly popupSizeWidthSmall = 400;
  readonly popupSizeHeightSmall = 400;

  loadingText = $localize`:@@loadingFile:Loading File...`;

  toolbarDownloadButtonOptions = {
    text: $localize`:@@downloadFile:Download File`,
    onClick: (e: any) => this.downloadFile(),
  };

  constructor(
    private documentsService: DocumentsService,
    private appGlobals: AppGlobals,
    private apiService: ApiBaseService
  ) {}
  ngOnDestroy(): void {
    if (this.fileDisplay) window.URL.revokeObjectURL(this.fileDisplay);
  }

  ngOnInit(): void {
    this.resetVisibility();
    this.popupVisible = false;
  }

  handleOnShowing(e: any) {
    e.component.option("width", this.popupSizeWidth);
    e.component.option("height", this.popupSizeHeight);
    // Jaco Bester 06/03/2025
  }
  handleOnHiding(e: any) {
    if (this.fileDisplay) window.URL.revokeObjectURL(this.fileDisplay);
  }

  private resetVisibility(): void {
    this.documentVisible = false;
    this.imageVisible = false;
    this.pdfVisible = false;
    this.generalVisible = false;
    this.popupSizeHeight = 600;
    this.popupSizeWidth = 800;
  }

  handlePDFLoaded(): void {
    setTimeout(() => {
      this.appGlobals.HideGlobalLoader();
    }, 1000);
  }

  private fileDelay(): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, 1000));
  }

  async loadFile(file: { fileType: string; fileUrl: string }): Promise<void> {
    this.fileDisplay = null;
    this.popupVisible = true;
    const response = await this.apiService.httpGETRequest(
      file.fileUrl,
      {},
      true,
      "blob"
    );
    if (response.hasError === true) {
      this.popupVisible = false;
      AppGlobals.ShowErrorMessage(response.feedback);
      return;
    }
    if (response instanceof Blob && response.size > 0) {
      // Check if it's a specific file type
      if (response.type.startsWith("image/")) {
        this.pdfVisible = false;
        this.imageVisible = true;
      } else if (response.type === "application/pdf") {
        this.pdfVisible = true;
        this.imageVisible = false;
      } else if (response.type.startsWith("video/")) {
        this.pdfVisible = false;
        this.imageVisible = false;
      } else if (response.type.startsWith("application/json")) {
        // Deserialize the JSON and show the error message
        const text = await response.text();
        try {
          const json = JSON.parse(text);
          if (json.hasError === true) {
            this.popupVisible = false;
            AppGlobals.ShowErrorMessage(
              $localize`:@@fileNotFound:File not found`
            );
            return;
          }
        } catch {
          this.popupVisible = false;
          AppGlobals.ShowErrorMessage(
            $localize`:@@internalServerError:Internal Server Error`
          );
        }
        return;
      }

      this.fileDisplay = URL.createObjectURL(response);
    } else {
      console.error("Invalid blob response");
    }
  }

  private async getAccessToken(): Promise<void> {
    try {
      const result = await this.documentsService.getUserDocumentAccessToken({});
      if (result.hasError) {
        AppGlobals.ShowErrorMessage(result.feedback);
        return;
      }
      this.currentAccessToken = result.accessToken;
    } catch (error) {
      console.error("Access token error:", error);
      AppGlobals.ShowErrorMessage(error);
      AppGlobals.GlobalLoaderVisible = false;
    }
  }

  getFileType(fileName: string): string {
    if (!fileName) return this.FILE_TYPES.GENERAL;

    const ext = fileName.split(".").pop()?.toLowerCase();
    if (!ext) return this.FILE_TYPES.GENERAL;

    const imageExts = ["bmp", "gif", "jpeg", "jpg", "png", "svg"];
    const documentExts = ["doc", "docx", "xls", "xlsx"];

    if (ext === "pdf") return this.FILE_TYPES.PDF;
    if (imageExts.includes(ext)) return this.FILE_TYPES.IMAGE;
    if (documentExts.includes(ext)) return this.FILE_TYPES.PDF;

    return this.FILE_TYPES.GENERAL;
  }

  // File loading methods
  public async LoadComponentFileItem(
    componentId: number,
    fileName: string,
    displayName?: string
  ): Promise<void> {
    await this.getAccessToken();
    if (!this.currentAccessToken) return;

    const type = this.getFileType(fileName);
    const encodedName = encodeURIComponent(fileName);
    const pdfParm = type === this.FILE_TYPES.PDF ? "true" : "false";

    const fileLink = this.buildApiUrl("GetComponentFileContent", {
      id: componentId,
      name: encodedName,
      pdf: pdfParm,
    });

    const downloadLink =
      AppConfig.APIEndPointBase +
      "/" +
      this.buildApiUrl("GetComponentFileContent", {
        id: componentId,
        name: encodedName,
        pdf: "false",
      });

    this.loadFile({ fileType: type, fileUrl: fileLink });
    this.currentFileItem = {
      name: displayName ?? fileName,
      downloadUrl: downloadLink,
      fileUrl: downloadLink,
      serverRelativeUrl: "",
      taskFile: false,
      propertyFile: false,
      commentFile: false,
      componentFile: true,
      componentId,
    };
  }

  public async LoadCommentFileItem(commentId: number, fileName: string) {
    await this.getAccessToken();
    if (!this.currentAccessToken) return;

    const type = this.getFileType(fileName);
    const encodedName = encodeURIComponent(fileName);
    const pdfParm = type === this.FILE_TYPES.PDF ? "true" : "false";

    const fileLink = this.buildApiUrl("GetCommentFileContent", {
      id: commentId,
      name: encodedName,
      pdf: pdfParm,
    });

    const downloadLink =
      AppConfig.APIEndPointBase +
      "/" +
      this.buildApiUrl("GetCommentFileContent", {
        id: commentId,
        name: encodedName,
        pdf: "false",
      });

    this.loadFile({ fileType: type, fileUrl: fileLink });
    this.currentFileItem = {
      name: fileName,
      downloadUrl: downloadLink,
      fileUrl: fileLink,
      serverRelativeUrl: "",
      taskFile: false,
      propertyFile: false,
      commentFile: true,
      componentFile: false,
      commentId: commentId,
    };
  }

  public async LoadPropertyFileItem(siteId: number, fileName: string) {
    await this.getAccessToken();
    if (!this.currentAccessToken) return;

    const type = this.getFileType(fileName);
    const encodedName = encodeURIComponent(fileName);
    const pdfParm = type === this.FILE_TYPES.PDF ? "true" : "false";

    const fileLink = this.buildApiUrl("GetPropertyFileContent", {
      id: siteId,
      name: encodedName,
      pdf: pdfParm,
    });

    const downloadLink =
      AppConfig.APIEndPointBase +
      "/" +
      this.buildApiUrl("GetPropertyFileContent", {
        id: siteId,
        name: encodedName,
        pdf: "false",
      });

    this.loadFile({ fileType: type, fileUrl: fileLink });
    this.currentFileItem = {
      name: fileName,
      downloadUrl: downloadLink,
      fileUrl: fileLink,
      serverRelativeUrl: "",
      taskFile: false,
      propertyFile: true,
      commentFile: false,
      componentFile: false,
      siteId: siteId,
    };
  }

  public async LoadTaskFileItem(fileId: number, fileName: string) {
    await this.getAccessToken();
    if (!this.currentAccessToken) return;

    const type = this.getFileType(fileName);
    const encodedName = encodeURIComponent(fileName);
    const pdfParm = type === this.FILE_TYPES.PDF ? "true" : "false";

    const fileLink = this.buildApiUrl("GetTaskFileContent", {
      id: fileId,
      name: encodedName,
      pdf: pdfParm,
    });

    const downloadLink =
      AppConfig.APIEndPointBase +
      "/" +
      this.buildApiUrl("GetTaskFileContent", {
        id: fileId,
        name: encodedName,
        pdf: "false",
      });

    this.loadFile({ fileType: type, fileUrl: fileLink });
    this.currentFileItem = {
      name: fileName,
      downloadUrl: downloadLink,
      fileUrl: fileLink,
      serverRelativeUrl: "",
      taskFile: true,
      propertyFile: false,
      commentFile: false,
      componentFile: false,
      fileId: fileId,
    };
  }

  public async LoadFileItem(fileItem: any) {
    await this.getAccessToken();
    if (!this.currentAccessToken) return;

    let encodedName = encodeURIComponent(fileItem.name);
    let encodedPath = encodeURIComponent(fileItem.serverRelativeUrl);

    const type = this.getFileType(encodedName);

    const pdfParm = type === this.FILE_TYPES.PDF ? "true" : "false";

    const fileLink = this.buildApiUrl("GetFileContent", {
      path: encodedPath,
      name: encodedName,
      pdf: pdfParm,
    });

    const downloadLink =
      AppConfig.APIEndPointBase +
      "/" +
      this.buildApiUrl("GetFileContent", {
        path: encodedPath,
        name: encodedName,
        pdf: "false",
      });

    this.loadFile({ fileType: type, fileUrl: fileLink });
    this.currentFileItem = {
      name: fileItem.name,
      downloadUrl: downloadLink,
      fileUrl: fileLink,
      serverRelativeUrl: "",
      taskFile: true,
      propertyFile: false,
      commentFile: false,
      componentFile: false,
    };
  }

  private buildApiUrl(
    endpoint: string,
    params: Record<string, string | number>
  ): string {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      queryParams.append(key, value.toString());
    });
    queryParams.append("token", this.currentAccessToken);
    return `DownloadService/${endpoint}?${queryParams.toString()}`;
  }

  private getMimeType(fileType: string): string {
    return (
      this.MIME_TYPES[fileType.toLowerCase()] || "application/octet-stream"
    );
  }

  private base64ToArrayBuffer(base64: string): Uint8Array<ArrayBuffer> {
    const binaryString = window.atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }

  private saveByteArray(
    reportName: string,
    fileType: string,
    byte: Uint8Array<ArrayBuffer>
  ): void {
    const mimeType = this.getMimeType(fileType);
    const blob = new Blob([byte], { type: mimeType });
    const link = window.URL.createObjectURL(blob);

    if (!reportName) {
      window.open(link)?.focus();
      return;
    }

    const anchorElement = document.createElement("a");
    document.body.appendChild(anchorElement);
    anchorElement.style.display = "none";
    anchorElement.href = link;
    anchorElement.download = reportName;
    anchorElement.click();

    window.URL.revokeObjectURL(link);
    anchorElement.remove();
  }

  handleFileError(): void {
    AppGlobals.ShowErrorMessage("File loading error occurred");
    this.appGlobals.HideGlobalLoader();
  }

  public async LoadContractFileItem(fileItem: any) {
    console.log("loading contract file item: ", fileItem);

    await this.getAccessToken();
    if (this.currentAccessToken == null || this.currentAccessToken == "")
      return;

    let encodedName = encodeURIComponent(fileItem.name);

    console.log("encoded name: ", encodedName);

    this.currentFileItem = fileItem;
    this.currentFileItem.taskFile = false;
    this.currentFileItem.propertyFile = false;
    this.currentFileItem.commentFile = false;
    this.currentFileItem.componentFile = false;

    let type = this.getFileType(fileItem.name);
    if (type == "") return;
    let pdfParm = type === this.FILE_TYPES.PDF ? "true" : "false";

    let fileLink = `${AppConfig.APIEndPointBase}/DownloadService/GetContractFileContent?name=${encodedName}&token=${this.currentAccessToken}&pdf=${pdfParm}`;
    let downloadUrl = `${AppConfig.APIEndPointBase}/DownloadService/GetContractFileContent?name=${encodedName}&token=${this.currentAccessToken}&pdf=false`;

    this.currentFileItem.fileUrl = fileLink;
    this.currentFileItem.downloadUrl = downloadUrl;

    this.loadFile({ fileType: type, fileUrl: fileLink });
    this.currentFileItem = {
      name: fileItem.name,
      downloadUrl: downloadUrl,
      fileUrl: fileLink,
      serverRelativeUrl: "",
      taskFile: true,
      propertyFile: false,
      commentFile: false,
      componentFile: false,
      fileId: null,
    };
  }

  async downloadFile(): Promise<void> {
    if (!this.currentFileItem?.downloadUrl) return;

    try {
      this.appGlobals.ShowGlobalLoader();

      const anchorElement = document.createElement("a");
      document.body.appendChild(anchorElement);
      anchorElement.style.display = "none";
      anchorElement.href = this.fileDisplay;
      anchorElement.download = this.currentFileItem.name;
      anchorElement.click();

      // Cleanup

      anchorElement.remove();
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      this.appGlobals.HideGlobalLoader();
    }
  }
}
